import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertOctagon, Save, RotateCcw, PenTool, Loader2, Info, SlidersHorizontal, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { riskService } from '../services/api';
import { useConfirmDialog } from '../components/DialogHost';

interface RiskConfig {
    p2p_high_volume_threshold: number;
    p2p_completion_min: number;
    p2p_new_merchant_days: number;
    p2p_new_merchant_vol_threshold: number;
    risk_score_critical: number;
    risk_score_high: number;
    risk_score_medium: number;
    signal_weights?: SignalWeights;
}

type SignalWeightKey =
    | 'entity_match'
    | 'sanctions'
    | 'multi_hop_exposure'
    | 'behavioural_patterns'
    | 'velocity'
    | 'p2p_signals'
    | 'external_threat_intel';

type SignalWeights = Record<SignalWeightKey, number>;

/** Order and wording of the weight sliders; keys match risk_rules_manager.SIGNAL_WEIGHT_KEYS. */
const SIGNAL_WEIGHTS: { key: SignalWeightKey; label: string; hint: string }[] = [
    { key: 'entity_match', label: 'Entity match', hint: 'Risk score of a known entity at the address' },
    { key: 'sanctions', label: 'Sanctions', hint: 'Sanctions-list hit (Critical floor at 1.0)' },
    { key: 'multi_hop_exposure', label: 'Multi-hop exposure', hint: 'Funds from / to risky counterparties' },
    { key: 'behavioural_patterns', label: 'Behavioural patterns', hint: 'Detected laundering / fraud patterns' },
    { key: 'velocity', label: 'Velocity', hint: 'Fund-through rate and dwell-time profile' },
    { key: 'p2p_signals', label: 'P2P signals', hint: 'Address seen in P2P advertisements' },
    { key: 'external_threat_intel', label: 'External threat intel', hint: 'Threat-intel and scam-report feeds' },
];

const DEFAULT_WEIGHTS: SignalWeights = {
    entity_match: 1,
    sanctions: 1,
    multi_hop_exposure: 1,
    behavioural_patterns: 1,
    velocity: 1,
    p2p_signals: 1,
    external_threat_intel: 1,
};

const DEFAULT_CONFIG: RiskConfig = {
    p2p_high_volume_threshold: 10000000,
    p2p_completion_min: 0.85,
    p2p_new_merchant_days: 30,
    p2p_new_merchant_vol_threshold: 100000,
    risk_score_critical: 0.7,
    risk_score_high: 0.4,
    risk_score_medium: 0.2,
    signal_weights: DEFAULT_WEIGHTS,
};

function weightsOf(c: RiskConfig): SignalWeights {
    return { ...DEFAULT_WEIGHTS, ...(c.signal_weights ?? {}) };
}

function isValidConfig(c: RiskConfig): string | null {
    const keys: (keyof RiskConfig)[] = [
        'p2p_high_volume_threshold', 'p2p_completion_min', 'p2p_new_merchant_days',
        'p2p_new_merchant_vol_threshold', 'risk_score_critical', 'risk_score_high', 'risk_score_medium',
    ];
    for (const k of keys) {
        if (typeof c[k] !== 'number' || Number.isNaN(c[k])) {
            return `Invalid number for ${k.replace(/_/g, ' ')}`;
        }
    }
    if (c.p2p_completion_min < 0 || c.p2p_completion_min > 1) {
        return 'Completion rate must be between 0 and 1 (e.g. 0.8 = 80%)';
    }
    if (!(c.risk_score_critical > c.risk_score_high && c.risk_score_high > c.risk_score_medium && c.risk_score_medium >= 0)) {
        return 'Bands must satisfy Critical > High > Medium ≥ 0';
    }
    if (c.risk_score_critical > 1 || c.risk_score_high > 1 || c.risk_score_medium > 1) {
        return 'Risk band scores must be between 0 and 1';
    }
    const w = weightsOf(c);
    for (const { key, label } of SIGNAL_WEIGHTS) {
        if (typeof w[key] !== 'number' || Number.isNaN(w[key]) || w[key] < 0 || w[key] > 2) {
            return `${label} weight must be between 0 and 2`;
        }
    }
    return null;
}

export const RiskRules: React.FC = () => {
    const { confirm, confirmDialog } = useConfirmDialog();
    const [config, setConfig] = useState<RiskConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedConfig, setEditedConfig] = useState<RiskConfig | null>(null);
    const [saving, setSaving] = useState(false);
    const [refOpen, setRefOpen] = useState(false);

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const data = await riskService.getConfig();
            const withWeights: RiskConfig = { ...data, signal_weights: weightsOf(data) };
            setConfig(withWeights);
            setEditedConfig(withWeights);
        } catch (error) {
            console.error('Failed to fetch risk config', error);
            setLoadError(true);
            setConfig(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!editedConfig) return;
        const err = isValidConfig(editedConfig);
        if (err) {
            toast.error(err);
            return;
        }
        const ok = await confirm({
            title: 'Save risk rules?',
            message: 'These thresholds and signal weights change how addresses, P2P merchants and entity risk chips are scored across the platform.',
            confirmText: 'Save',
        });
        if (!ok) return;
        setSaving(true);
        try {
            await riskService.updateConfig(editedConfig);
            setConfig(editedConfig);
            setIsEditing(false);
            toast.success('Risk rules updated');
        } catch {
            toast.error('Could not save rules');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (key: keyof RiskConfig, value: string) => {
        if (!editedConfig) return;
        const n = parseFloat(value);
        setEditedConfig({
            ...editedConfig,
            [key]: value === '' || Number.isNaN(n) ? Number.NaN : n,
        });
    };

    const handleWeightChange = (key: SignalWeightKey, value: string) => {
        if (!editedConfig) return;
        const n = parseFloat(value);
        setEditedConfig({
            ...editedConfig,
            signal_weights: { ...weightsOf(editedConfig), [key]: Number.isNaN(n) ? 1 : Math.round(n * 10) / 10 },
        });
    };

    /** Loads the built-in rules into the editor; nothing is saved until Save. */
    const handleResetToDefaults = async () => {
        let defaults: RiskConfig = DEFAULT_CONFIG;
        try {
            const data = await riskService.getDefaults();
            defaults = { ...data, signal_weights: weightsOf(data) };
        } catch {
            // Older backend without /risk/config/defaults: use the built-in copy.
        }
        setEditedConfig(defaults);
        setIsEditing(true);
        toast.info('Defaults loaded. Press Save to apply them.');
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" /> Loading risk rules…
            </div>
        );
    }

    if (loadError || !config) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="border border-dashed border-border rounded-lg bg-card p-10 text-center space-y-3">
                    <AlertTriangle className="w-8 h-8 mx-auto text-muted-foreground" />
                    <h1 className="text-lg font-semibold text-foreground">Could not load Risk Rules</h1>
                    <p className="text-sm text-muted-foreground">Check your connection and try again.</p>
                    <button
                        type="button"
                        onClick={fetchConfig}
                        className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const display = isEditing ? editedConfig! : config;

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {confirmDialog}
            <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Risk Rules</h1>
                    <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
                        Thresholds that decide when a merchant or address shows as Critical, High, Medium, or Low —
                        on P2P Intelligence, entity risk chips, and investigation summaries.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleResetToDefaults}
                        data-testid="risk-rules-reset-defaults"
                        className="px-4 py-2 border border-border rounded-md text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm"
                    >
                        <Undo2 className="w-4 h-4" /> Reset to defaults
                    </button>
                    {isEditing ? (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    setEditedConfig(config);
                                    setIsEditing(false);
                                }}
                                className="px-4 py-2 text-muted-foreground hover:text-foreground flex items-center gap-2 text-sm"
                            >
                                <RotateCcw className="w-4 h-4" /> Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={saving}
                                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-2 text-sm disabled:opacity-50"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setIsEditing(true)}
                            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 flex items-center gap-2 text-sm"
                        >
                            <PenTool className="w-4 h-4" /> Edit rules
                        </button>
                    )}
                </div>
            </div>

            {/* Investigation risk bands */}
            <section className="bg-card border border-border rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-primary" />
                        Investigation risk bands
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        Impact: changing these numbers moves the cut-offs for Critical / High / Medium badges on
                        Workbench risk chips and LE report escalation language.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {([
                        { key: 'risk_score_critical' as const, label: 'Critical (≥)', hint: 'Freeze / escalate immediately' },
                        { key: 'risk_score_high' as const, label: 'High (≥)', hint: 'Investigate urgently' },
                        { key: 'risk_score_medium' as const, label: 'Medium (≥)', hint: 'Monitor and review' },
                    ]).map(band => (
                        <div key={band.key} className="bg-muted/30 border border-border rounded-lg p-4">
                            <label className="block text-sm font-medium text-foreground mb-1">{band.label}</label>
                            {isEditing ? (
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="1"
                                    className="w-full bg-background border border-border rounded px-3 py-2 text-foreground"
                                    value={Number.isNaN(display[band.key]) ? '' : display[band.key]}
                                    onChange={(e) => handleChange(band.key, e.target.value)}
                                />
                            ) : (
                                <div className="text-xl font-mono text-foreground">{display[band.key].toFixed(2)}</div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">{band.hint}</p>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground">
                    Low is anything below Medium ({display.risk_score_medium.toFixed(2)}). Keep Critical &gt; High &gt; Medium.
                </p>
            </section>

            {/* Signal weights */}
            <section className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="risk-signal-weights">
                <div>
                    <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <SlidersHorizontal className="w-5 h-5 text-primary" />
                        Signal weights
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        Impact: each weight multiplies that signal&apos;s contribution to an address&apos;s composite
                        risk score (1.0 = standard, 0 = ignore, 2.0 = double). The score is capped at 1.0 and then
                        placed in the bands above.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {SIGNAL_WEIGHTS.map(({ key, label, hint }) => {
                        const value = weightsOf(display)[key];
                        return (
                            <div key={key} className="bg-muted/30 border border-border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor={`weight-${key}`} className="text-sm font-medium text-foreground">{label}</label>
                                    <span className="text-sm font-mono text-foreground">{value.toFixed(1)}×</span>
                                </div>
                                <input
                                    id={`weight-${key}`}
                                    type="range"
                                    min="0"
                                    max="2"
                                    step="0.1"
                                    disabled={!isEditing}
                                    aria-label={`${label} weight`}
                                    data-testid={`risk-weight-${key}`}
                                    className="w-full accent-primary disabled:opacity-60"
                                    value={value}
                                    onChange={(e) => handleWeightChange(key, e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground mt-2">{hint}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* P2P rules */}
            <section className="bg-card border border-border rounded-xl p-6 space-y-4">
                <div>
                    <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                        <AlertOctagon className="w-5 h-5 text-primary" />
                        P2P identification rules
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                        Impact: these change how P2P Intelligence flags high-volume, low-completion, or brand-new merchants —
                        not the Workbench chain-risk bands above.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <label className="block text-sm font-medium text-foreground mb-1">High volume threshold (INR)</label>
                        {isEditing ? (
                            <input
                                type="number"
                                className="w-full bg-background border border-border rounded px-3 py-2 text-foreground"
                                value={Number.isNaN(display.p2p_high_volume_threshold) ? '' : display.p2p_high_volume_threshold}
                                onChange={(e) => handleChange('p2p_high_volume_threshold', e.target.value)}
                            />
                        ) : (
                            <div className="text-xl font-mono text-foreground">
                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(display.p2p_high_volume_threshold)}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Merchants above this volume get a higher P2P risk contribution.</p>
                    </div>
                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <label className="block text-sm font-medium text-foreground mb-1">Completion rate minimum (0–1)</label>
                        {isEditing ? (
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="1"
                                className="w-full bg-background border border-border rounded px-3 py-2 text-foreground"
                                value={Number.isNaN(display.p2p_completion_min) ? '' : display.p2p_completion_min}
                                onChange={(e) => handleChange('p2p_completion_min', e.target.value)}
                            />
                        ) : (
                            <div className="text-xl font-mono text-foreground">
                                {(display.p2p_completion_min * 100).toFixed(0)}%
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Below this completion rate, merchants are treated as higher risk.</p>
                    </div>
                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <label className="block text-sm font-medium text-foreground mb-1">New merchant window (days)</label>
                        {isEditing ? (
                            <input
                                type="number"
                                className="w-full bg-background border border-border rounded px-3 py-2 text-foreground"
                                value={Number.isNaN(display.p2p_new_merchant_days) ? '' : display.p2p_new_merchant_days}
                                onChange={(e) => handleChange('p2p_new_merchant_days', e.target.value)}
                            />
                        ) : (
                            <div className="text-xl font-mono text-foreground">{display.p2p_new_merchant_days} days</div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Accounts younger than this are treated as “new”.</p>
                    </div>
                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                        <label className="block text-sm font-medium text-foreground mb-1">New merchant volume threshold (INR)</label>
                        {isEditing ? (
                            <input
                                type="number"
                                className="w-full bg-background border border-border rounded px-3 py-2 text-foreground"
                                value={Number.isNaN(display.p2p_new_merchant_vol_threshold) ? '' : display.p2p_new_merchant_vol_threshold}
                                onChange={(e) => handleChange('p2p_new_merchant_vol_threshold', e.target.value)}
                            />
                        ) : (
                            <div className="text-xl font-mono text-foreground">
                                {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(display.p2p_new_merchant_vol_threshold)}
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">New accounts moving more than this volume are flagged.</p>
                    </div>
                </div>
            </section>

            {/* What each band means */}
            <section className="bg-card border border-border rounded-xl p-6 space-y-4">
                <h2 className="text-lg font-semibold text-foreground">What each band means for officers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { name: 'Critical', cut: `≥ ${display.risk_score_critical}`, action: 'Escalate / freeze path', examples: 'Sanctions hit, known scammer, court defendant' },
                        { name: 'High', cut: `≥ ${display.risk_score_high}`, action: 'Investigate urgently', examples: 'Bad-actor exposure, suspicious patterns' },
                        { name: 'Medium', cut: `≥ ${display.risk_score_medium}`, action: 'Monitor & review', examples: 'Mixer use, new high-volume account' },
                        { name: 'Low', cut: `< ${display.risk_score_medium}`, action: 'No special action', examples: 'Clean history, known exchange' },
                    ].map(b => (
                        <div key={b.name} className="border border-border rounded-lg p-4 bg-muted/20">
                            <div className="font-semibold text-foreground text-sm">{b.name}</div>
                            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{b.cut}</div>
                            <p className="text-xs text-foreground/80 mt-2">{b.examples}</p>
                            <p className="text-xs font-medium text-primary mt-2">{b.action}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Reference only */}
            <section className="border border-dashed border-border rounded-xl p-4">
                <button
                    type="button"
                    onClick={() => setRefOpen(v => !v)}
                    className="w-full flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                    <Info className="w-4 h-4" />
                    Reference only — static signals &amp; modifiers (not editable here)
                    <span className="ml-auto text-xs">{refOpen ? 'Hide' : 'Show'}</span>
                </button>
                {refOpen && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm text-muted-foreground">
                        <ul className="space-y-2 list-disc pl-5">
                            <li>Threat intel match → score override toward Critical</li>
                            <li>Sanctions list → Critical path</li>
                            <li>eCourts case → High minimum</li>
                        </ul>
                        <ul className="space-y-2 list-disc pl-5">
                            <li>P2P presence → adds risk contribution</li>
                            <li>High-risk exposure → adds contribution</li>
                            <li>New account + high volume → adds contribution</li>
                        </ul>
                    </div>
                )}
            </section>
        </div>
    );
};
